<template>
  <div class="uk-modal-dialog uk-modal-body uk-margin-auto-vertical form">
    <Form @submit="onBeforeSubmit" :validation-schema="validationSchema" ref="form" :initial-values="formValues">
      <div class="uk-modal-header form-header">
        <span class="uk-text-bold uk-text-large">{{ criterion && criterion.id ? "人材基準編集" : "人材基準新規作成" }}</span>
      </div>
      <div class="uk-modal-body">
        <FormError :errors="globalErrors" />
        <div class="field">
          <Field name="name" v-slot="{ field }">
            <FieldLabel title="人材基準名" :required="true" />
            <input class="uk-input" type="text" v-bind="field" />
          </Field>
          <FieldError name="name" />
        </div>
        <div class="criteria" v-if="criteria">
          <div v-for="(item, i) in formValues.criteria" :key="item.id">
            <div class="field column-2">
              <FieldLabel :title="`項目${i + 1}`" />
              <div>
                <Field :name="`criteria[${i}].code`" v-slot="{ field }">
                  <select class="uk-select" v-bind="field" @change="onStatusChange">
                    <option v-for="option in criteria" :key="option.code" :value="option.code">{{ option.name }}</option>
                  </select>
                </Field>
              </div>
              <div class="number-field">
                <Field :name="`criteria[${i}].thresholdMin`" v-slot="{ field }">
                  <input class="uk-input number" type="number" v-bind="field" />
                </Field>
              </div>
              <div class="column-text">&nbsp;～&nbsp;</div>
              <div class="number-field">
                <Field :name="`criteria[${i}].thresholdMax`" v-slot="{ field }">
                  <input class="uk-input number" type="number" v-bind="field" />
                </Field>
              </div>
              <IconButton icon="minus" @click="onCriterionDeleteButtonClick(item)" />
            </div>
            <div class="error-container">
              <FieldError :name="`criteria[${i}].code`" />
              <FieldError :name="`criteria[${i}].thresholdMin`" />
              <FieldError :name="`criteria[${i}].thresholdMax`" />
            </div>
          </div>
          <IconButton icon="plus" @click="onCriterionAddButtonClick" />
        </div>
      </div>
      <div class="uk-modal-footer uk-text-right form-action">
        <div>
          <ActionButton title="戻る" class="uk-modal-close" />
        </div>
        <div class="form-action-main">
          <ActionButton title="確定" color="primary" type="submit" :disabled="processing" />
          <ActionButton v-if="criterion" title="削除" @click="onDeleteButtonClick" color="warn" />
        </div>
      </div>
    </Form>
  </div>
</template>
<style lang="scss" scoped>
@include modal-form;
.field.column-2 {
  align-items: center;
  margin-bottom: 0;
  label {
    margin-right: $spacing-ex-small;
  }
}
.number-field {
  width: 70px;
  flex: unset !important;
  .uk-input {
    width: 100%;
  }
}
.column-text {
  width: 20px;
  flex: unset !important;
}
.error-container {
  margin-bottom: $spacing-medium;
}
</style>
<script lang="ts">
import { Options } from "vue-class-component";
import { Prop } from "vue-property-decorator";
import FormMixin from "../../helpers/mixins/form";
import { ClientApi, SystemApi, GetHrCriterion, GetHrCriterionItem, GetHrCriterionCriteriaItem } from "../../autogen";
import { Field, Form } from "vee-validate";
import FieldLabel from "../atoms/FieldLabel.vue";
import * as yup from "yup";
import FieldError from "../atoms/FieldError.vue";
import FormError from "../atoms/FormError.vue";
import ActionButton from "@/components/atoms/ActionButton.vue";
import IconButton from "@/components/atoms/IconButton.vue";
type Criteria = GetHrCriterionCriteriaItem & { id?: number };
type FormValues = { name: string; criteria: Criteria[] };
@Options({
  components: {
    Field,
    Form,
    FieldLabel,
    FieldError,
    ActionButton,
    FormError,
    IconButton
  }
})
export default class HrCriterionForm extends FormMixin {
  @Prop({ type: Object }) criterion?: GetHrCriterion;
  formName = "人材基準";
  formValues: FormValues = { name: "", criteria: [] };
  criteria!: GetHrCriterionItem[];
  validationSchema!: yup.AnyObjectSchema;
  async beforeCreate() {
    this.validationSchema = yup.object({
      name: yup
        .string()
        .trim()
        .required(this.$t("input_required", { field: "人材基準名" })),
      criteria: yup.array().of(
        yup.object().shape({
          code: yup.string().required(this.$t("input_required", { field: "基準" })),
          thresholdMin: yup
            .number()
            .nullable(true)
            .transform(v => (v === "" || Number.isNaN(v) ? null : v))
            .required(this.$t("input_required", { field: "閾値 (最小)" }))
            .min(0, this.$t("input_min", { field: "閾値 (最小)", value: "0" }))
            .max(100, this.$t("input_max", { field: "閾値 (最小)", value: "100" })),
          thresholdMax: yup
            .number()
            .nullable(true)
            .transform(v => (v === "" || Number.isNaN(v) ? null : v))
            .required(this.$t("input_required", { field: "閾値 (最大)" }))
            .min(0, this.$t("input_min", { field: "閾値 (最大)", value: "0" }))
            .max(100, this.$t("input_max", { field: "閾値 (最大)", value: "100" }))
            // @ts-ignore
            .when("thresholdMin", (min, schema) => schema.min(min, this.$t("input_min", { field: "閾値 (最大)", value: min })))
        })
      )
    });
  }
  async beforeMount() {
    const response = await new SystemApi().getHrCriteriaList({});
    this.criteria = response.data;
    this.formValues = this.criterion
      ? {
          name: this.criterion.name,
          criteria: JSON.parse(JSON.stringify(this.criterion.criteria))
        }
      : { name: "", criteria: [] };
  }
  async onBeforeSubmit(values: FormValues) {
    let criteria = values.criteria;
    if (criteria && criteria.length) {
      criteria = criteria.map(criterion =>
        Object.assign({}, criterion, {
          thresholdMin: parseFloat((criterion.thresholdMin as unknown) as string),
          thresholdMax: parseFloat((criterion.thresholdMax as unknown) as string)
        })
      );
    }
    await this.onSubmit(async () => {
      if (this.criterion) {
        await new ClientApi().updateHrCriteria({
          hrCriterionId: this.criterion.id,
          body: { name: values.name, criteria }
        });
      } else {
        await new ClientApi().createHrCriteria({
          createHrCriteriaRequest: { name: values.name, criteria }
        });
      }
      this.$emit("submitted");
    });
  }
  async onDeleteButtonClick() {
    this.onDelete(async () => {
      await new ClientApi().deleteHrCriteria({
        hrCriterionId: this.criterion!.id
      });
    });
  }
  onCriterionAddButtonClick() {
    this.formValues.criteria.push({
      code: "",
      thresholdMin: 0,
      thresholdMax: 100,
      id: Date.now()
    });
  }
  onCriterionDeleteButtonClick(item: GetHrCriterionCriteriaItem) {
    const index = this.formValues.criteria.indexOf(item);
    this.formValues.criteria.splice(index, 1);
  }
}
</script>
